import { z } from 'zod';

interface SchemaContext {
    isCreate: boolean;
    t: (key: string) => string;
}

export const settingsSchema = ({ isCreate, t }: SchemaContext) => {
    const passwordRules = z.string()
        .min(8)
        .regex(/(.*[a-z].*)/, t('There must be at least one lowercase character'))
        .regex(/(.*[A-Z].*)/, t('There must be at least one uppercase character'))
        .regex(/(.*\d.*)/, t('There must be at least one numberic character'))
        .regex(/[!@#$%^&*(),.?":{}|<>]/, t('There must be at least one symbol'));

    const passwordSchema = isCreate
        ? passwordRules
        : z.union([
            z.literal(''),
            passwordRules,
        ]);

    return z.object({
        active_workspace_id: z.union([
            z.literal(''),
            z.null(),
            z.coerce.number(),
        ]).optional(),
        first_name: z.string().min(1),
        last_name: z.string().min(1),
        locale: z.string().min(1),
        password: passwordSchema,
        password_confirmation: isCreate ? z.string().min(1) : z.string().optional(),
        price_display: z.string().min(1),
    }).superRefine((data, ctx) => {
        if (!isCreate && (data.password ?? '') === '') {
            return;
        }
        if (data.password !== data.password_confirmation) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [
                    'password_confirmation',
                ],
                message: 'forms.generic.password_must_match',
            });
        }
    });
};

export type SettingsValues = {
    active_workspace_id: number | null | '';
    first_name: string;
    last_name: string;
    locale: string;
    password: string;
    password_confirmation: string;
    price_display: string;
};

export const settingsDefaults = (): SettingsValues => {
    return {
        active_workspace_id: null,
        price_display: 'all',
        first_name: '',
        last_name: '',
        locale: '',
        password: '',
        password_confirmation: '',
    };
};
